import { useMemo } from 'react';
import { costFormater } from '@common-fe/common-fe';

import { useOrphanModeStore } from '@/modules/core/store';
import { useGetIrsMaximums } from '@/modules/transaction/queries/useGetIrsMaximums.query';
import { useAuthStore } from '@/modules/user';
import { useEmployeeDataQuery } from '@/modules/user/queries/useEmployeeData.query';

import { ContributionAchievementsName, useGetContributionsHistoryQuery } from '../queries/useGetContributionsHistory.query';

import { useGetIrsLimitsQuery } from './useGetIrsLimits.query';

const MIN_CATCH_UP_ELECTION = 1000;

interface ContributionAchievementsTrackerProps {
  personId: string;
  taxYear?: string;
  accountId?: string;
  catchUpElection?: number;
  accountType?: string;
  planId: string;
}

export const useContributionAchievementsTracker = (props: ContributionAchievementsTrackerProps) => {
  const {
    personId,
    taxYear,
    accountId,
    catchUpElection,
    accountType,
    planId,
  } = props;

  const { linkedEmployee } = useOrphanModeStore();
  const linkedEmployeeId = useMemo(() => linkedEmployee?.employeeId, [linkedEmployee]);
  const { rawData } = useEmployeeDataQuery(linkedEmployeeId, !!linkedEmployeeId);
  const linkedPersonId = useMemo(() => rawData?.person_id ? `${rawData?.person_id}` : undefined, [rawData]);

  const {
    contributionAchievements,
    formattedData,
    isLoading: contributionHistoryLoading,
    refetch: refetchContributionHistory,
  } = useGetContributionsHistoryQuery(linkedPersonId || personId, taxYear, accountId);
  const { user } = useAuthStore();
  const employeeId = useMemo(() => linkedEmployee?.employeeId || user?.employee?.id, [linkedEmployee, user]);
  const { irsLimits } = useGetIrsLimitsQuery({
    taxYear,
    employeePlanPairs: [{
      employeeId,
      planId,
    }],
  });
  const maxContributionValue = useMemo(() => {
    if (!planId || !employeeId) {
      return 0;
    }

    const planIdToContributionAmount = irsLimits
      ?.find((item) => item?.employeeId === employeeId)
        // @ts-ignore
        ?.planIdToContributionAmount?.[planId];
    const planIdToElectionLimitAmount = irsLimits
      ?.find((item) => item?.employeeId === employeeId)
        // @ts-ignore
        ?.planIdToElectionLimitAmount?.[planId];

    return Math.min(
      ...[
        planIdToContributionAmount,
        planIdToElectionLimitAmount,
      ].filter((item) => typeof item === 'number'),
    );
  },
    [irsLimits, employeeId, planId]);

  const { lastDayForContribution } = useGetIrsMaximums({
    ...accountType ? { type: accountType } : {},
  });

  const isWithCatchUp = useMemo(() => formattedData?.isWithCatchUp, [formattedData]);

  const preparedContributionAchievements = useMemo(() => {
    if (isWithCatchUp) {
      const catchUpItem = {
        id: ContributionAchievementsName.CATCH_UP,
        name: ContributionAchievementsName.CATCH_UP,
        maxValue: catchUpElection || MIN_CATCH_UP_ELECTION,
        maxInitialValue: catchUpElection || MIN_CATCH_UP_ELECTION,
        maxValueDescription: costFormater(catchUpElection || MIN_CATCH_UP_ELECTION, true),
      };
      return [...contributionAchievements, catchUpItem];
    }

    return contributionAchievements;
  }, [contributionAchievements, catchUpElection, isWithCatchUp]);

  const currentContributionValue = useMemo(() => formattedData.currentValue, [formattedData]);

  return {
    preparedContributionAchievements,
    lastDayForContribution,
    formattedData,
    contributionHistoryLoading,
    isWithCatchUp,
    maxContributionValue,
    currentContributionValue,
    refetchContributionHistory,
  };
};
